export const categories = [
  "All",
  "Cyber Security",
  "Cloud Computing",
  "Networking",
  "AI & Automation",
];

export const posts = [
  {
    cover: "shield",
    tag: "Cyber Security",
    date: "Oct 8, 2026",
    iso: "2026-10-08",
    read: 7,
    title:
      "Zero Trust in Practice: Securing a Hybrid Workforce Without Slowing It Down",
    text: "Identity-first access, device checks and least privilege sound heavy on paper. Here is how teams roll them out in weeks, not quarters.",
    slug: "zero-trust-in-practice-securing-a-hybrid-workforce-without-s",
    keywords: ["Zero trust", "Identity", "Hybrid work"],
    heroImage: "/assets/images/blog/zero-trust-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Hybrid work broke the idea of a trusted office network. Zero trust replaces it with a simple rule: verify every request, every time. The challenge is doing that without frustrating the people you are trying to protect.",
      },
      {
        type: "h2",
        text: "What zero trust actually means",
      },
      {
        type: "p",
        text: "Zero trust is not a product you can buy. It is an approach built on three principles: verify explicitly, grant the least access needed, and assume a breach can happen. Instead of trusting anyone who is already inside the network, every request is checked against who the person is, which device they are using and what they are trying to reach.",
      },
      {
        type: "h2",
        text: "Start with identity",
      },
      {
        type: "p",
        text: "Most breaches begin with a stolen password. Putting every application behind a single identity provider gives you one place to enforce multi-factor authentication, review access and switch off accounts the day someone leaves.",
      },
      {
        type: "p",
        text: "Move staff to single sign-on so there are fewer passwords to steal, and require multi-factor authentication for everyone, with phishing-resistant methods for administrators. At the same time, remove dormant accounts, shared logins and old contractor access.",
      },
      {
        type: "h2",
        text: "Check the device, not just the person",
      },
      {
        type: "p",
        text: "A correct password on an unpatched laptop is still a risk. Conditional access policies can ask whether a device is encrypted, up to date and managed before allowing access to sensitive data. Staff on personal devices can still be productive, with browser-only access that keeps company files off the machine.",
      },
      {
        type: "p",
        text: "Security that people work around is not security, so measure friction as carefully as you measure risk.",
      },
      {
        type: "h2",
        text: "Shrink the blast radius",
      },
      {
        type: "p",
        text: "Assume that one account will eventually be compromised, and design so the damage stays small. Segment networks and cloud environments, give people only the access their role needs, and use just-in-time elevation for administrators so powerful permissions exist only when they are required.",
      },
      {
        type: "h2",
        text: "A 90-day rollout plan",
      },
      {
        type: "p",
        text: "You do not need a multi-year programme to make real progress. A phased plan keeps changes small enough that staff barely notice them.",
      },
      {
        type: "p",
        text: "In the first 30 days, list your users, devices and applications, then enable single sign-on and MFA for your most important systems. Over the next 30, enrol laptops and phones, define device compliance rules and switch on conditional access in report-only mode. In the final stretch, enforce the policies, restrict administrator rights and set up alerts for unusual sign-ins.",
      },
      {
        type: "h2",
        text: "How to measure success",
      },
      {
        type: "p",
        text: "Track a handful of simple numbers: the share of users on MFA, the share of devices that meet your policy, the time it takes to remove a leaver's access and the number of help-desk tickets caused by sign-in friction. If risk goes down and tickets stay flat, the rollout is working.",
      },
      {
        type: "p",
        text: "If you would like a second pair of eyes on your current setup, our team can run a short review and turn it into a plan that fits your size and budget.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Identity is the new perimeter, so strengthen sign-in before anything else.",
          "Device health is part of every access decision, not an afterthought.",
          "Small, steady rollouts beat a big-bang project that disrupts everyone.",
        ],
      },
    ],
  },
  {
    cover: "cloud",
    tag: "Cloud Computing",
    date: "Sep 29, 2026",
    iso: "2026-09-29",
    read: 6,
    title:
      "Cloud Cost Control: Seven Ways to Trim Your Azure and AWS Bill Without Hurting Performance",
    text: "Rightsizing, reserved capacity and storage tiering often uncover real savings. A practical walkthrough of where to look first.",
    slug: "cloud-cost-control-seven-ways-to-trim-your-azure-and-aws-bil",
    keywords: ["Cloud cost", "Azure", "AWS"],
    heroImage: "/assets/images/blog/cloud-cost-control-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Cloud bills rarely grow because of one big mistake. They grow through dozens of small ones: a test server nobody switched off, a database sized for a launch that never came, backups kept for years on the most expensive storage. The good news is that the same handful of fixes works on both Azure and AWS.",
      },
      {
        type: "h2",
        text: "Why it is worth the effort",
      },
      {
        type: "p",
        text: "In Flexera's 2026 State of the Cloud survey, organisations estimated that 29% of their infrastructure and platform spending is wasted, and 85% said keeping cloud costs under control is one of their biggest challenges. Most of that waste sits in a few predictable places, which is why the steps below usually pay for themselves quickly.",
      },
      {
        type: "h2",
        text: "1. Make every resource someone's responsibility",
      },
      {
        type: "p",
        text: "You cannot fix costs that nobody owns. Agree a short tagging standard, such as owner, application, environment and cost centre, and enforce it with Azure Policy or AWS tag policies so untagged resources are flagged or blocked. Give each team a weekly view of its own spend in Azure Cost Management or AWS Cost Explorer, and set budgets with alerts so surprises arrive within days rather than at month end.",
      },
      {
        type: "h2",
        text: "2. Remove what nobody uses",
      },
      {
        type: "p",
        text: "Start with the easy wins: unattached disks, old snapshots, idle load balancers, forgotten test environments and public IP addresses that are not connected to anything. Unused addresses are not free; AWS has charged for every public IPv4 address since February 2024. Review these items monthly, because they reappear faster than you expect.",
      },
      {
        type: "h2",
        text: "3. Rightsize before you commit",
      },
      {
        type: "p",
        text: "Many servers and databases are sized for a peak that rarely arrives. AWS Compute Optimizer and Azure Advisor both study real utilisation and suggest smaller or better-suited instance types. Rightsize first and commit second; otherwise you lock in discounts on capacity you do not need. Newer instance generations, and Arm-based options such as AWS Graviton where your software supports them, are often cheaper for the same work, so benchmark your own workload before switching.",
      },
      {
        type: "h2",
        text: "4. Commit to your baseline, not your peak",
      },
      {
        type: "p",
        text: "For workloads that run around the clock, commitment discounts are the biggest single lever. AWS Savings Plans offer up to 72% off On-Demand prices in return for committing to a fixed hourly spend for one or three years. On Azure, reservations offer up to 72% off pay-as-you-go prices for virtual machines, and the more flexible Azure savings plan for compute offers up to 65%. The top discounts come with three-year terms, so cover only the steady baseline you are confident about and leave spiky demand on standard pricing.",
      },
      {
        type: "h2",
        text: "5. Run flexible work on spare capacity",
      },
      {
        type: "p",
        text: "Batch jobs, build pipelines, rendering and other work that can be interrupted can run on the providers' spare capacity at a steep discount: up to 90% off with both AWS Spot Instances and Azure Spot Virtual Machines. The catch is that the capacity can be reclaimed at short notice, about two minutes on AWS and as little as 30 seconds on Azure, so use it only for jobs that can save progress and restart.",
      },
      {
        type: "h2",
        text: "6. Switch off non-production outside working hours",
      },
      {
        type: "p",
        text: "Development and test environments rarely need to run all week. A week has 168 hours, while a ten-hour, five-day schedule needs only 50 of them, so stopping non-production machines outside office hours cuts their running hours by about 70%. Both clouds offer ready-made scheduling tools, and infrastructure as code makes it easy to rebuild an environment when someone needs it.",
      },
      {
        type: "h2",
        text: "7. Put data on the right storage tier",
      },
      {
        type: "p",
        text: "Storage costs creep up quietly. Amazon S3 Intelligent-Tiering moves objects between access tiers automatically based on how often they are read, and Azure Blob Storage offers hot, cool, cold and archive tiers with lifecycle rules to move data between them. Read the fine print: cooler tiers cost more to read from and have minimum storage periods, so they suit backups and archives rather than files you open every day. Set retention rules for logs and snapshots too, so you stop paying to keep data nobody needs.",
      },
      {
        type: "h2",
        text: "Keep performance in the picture",
      },
      {
        type: "p",
        text: "Savings that slow your customers down are a false economy. Track response times alongside spend, change one thing at a time and measure unit costs such as cost per order or per active user. If those numbers fall while performance holds steady, the savings are real.",
      },
      {
        type: "p",
        text: "If you would like a second opinion on your Azure or AWS bill, our team can review your usage and hand you a prioritised list of savings.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Tag everything and give each team visibility of its own spend.",
          "Rightsize first, then use commitment discounts only for your steady baseline.",
          "Spot capacity, schedules and storage tiers cut costs without touching production performance.",
        ],
      },
    ],
  },
  {
    cover: "wifi",
    tag: "Networking",
    date: "Sep 4, 2026",
    iso: "2026-09-04",
    read: 5,
    title: "Wi-Fi 7 for the Office: Is It Time to Upgrade Your Network?",
    text: "Faster speeds are only part of the story. We look at when new access points pay off and when your cabling is the real bottleneck.",
    slug: "wi-fi-7-for-the-office-is-it-time-to-upgrade-your-network",
    keywords: ["Wi-Fi 7", "Office network", "Structured cabling"],
    heroImage: "/assets/images/blog/wifi-7-office-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Wi-Fi 7 access points are now easy to buy, and their spec sheets promise multi-gigabit speeds. Whether an upgrade makes sense for your office depends less on the radio than on three questions: what your laptops and phones support, what your cabling can carry and what your current network is actually struggling with.",
      },
      {
        type: "h2",
        text: "What Wi-Fi 7 brings",
      },
      {
        type: "p",
        text: "Wi-Fi 7 is based on the IEEE 802.11be standard, and the Wi-Fi Alliance has certified products for it since January 2024. For offices, its most useful feature is Multi-Link Operation, which lets a device use more than one band at the same time and keeps connections steadier when one band gets crowded. It also adds wider 320 MHz channels in the 6 GHz band and denser 4096-QAM modulation, which raise peak speeds for devices close to the access point.",
      },
      {
        type: "h2",
        text: "Check what your country allows in 6 GHz",
      },
      {
        type: "p",
        text: "Much of Wi-Fi 7's extra capacity lives in the 6 GHz band, so local rules matter. In January 2026, India's Department of Telecommunications exempted the lower part of the band, 5925 to 6425 MHz, from licensing for low-power and very-low-power wireless access, including Wi-Fi. The rules carve out some settings, such as oil platforms, vehicles, drones and aircraft below 10,000 feet.",
      },
      {
        type: "p",
        text: "That 500 MHz slice leaves room for several 160 MHz channels but only one 320 MHz channel at a time, a position similar to Europe's, whereas the United States has opened the full 1,200 MHz band. In a busy office, narrower channels are often the better choice anyway, because they let neighbouring access points avoid interfering with each other.",
      },
      {
        type: "h2",
        text: "Check your devices first",
      },
      {
        type: "p",
        text: "An access point can only use Wi-Fi 7 features with devices that support them. If most of your laptops use Wi-Fi 5 or Wi-Fi 6, they will connect to a new access point using their own older standard and see only modest gains. Look at your hardware refresh plan: if a large share of laptops is due for replacement in the next year or two, the new models will probably support Wi-Fi 7 or 6E, and the case for new access points becomes much stronger.",
      },
      {
        type: "h2",
        text: "Your cabling may be the real bottleneck",
      },
      {
        type: "p",
        text: "Every access point connects to the network through a copper cable, and a Wi-Fi 7 access point can move more traffic than a standard 1 Gbps link can carry. The IEEE 802.3bz standard, published in 2016, added 2.5 and 5 Gbps Ethernet designed for cabling that is already installed: 2.5 Gbps over Cat5e and 5 Gbps over Cat6, both at the usual 100-metre reach. Full 10 Gbps over copper needs Cat6A to cover 100 metres; on Cat6 it is limited to roughly 55 metres.",
      },
      {
        type: "p",
        text: "Before buying access points, find out what is actually in your walls and ceilings. Existing Cat5e runs are usually fine for 2.5 Gbps, which is plenty for most office access points. If you are pulling new cable anyway, Cat6A is the safer long-term choice.",
      },
      {
        type: "h2",
        text: "Power and switches",
      },
      {
        type: "p",
        text: "Access points draw their power through the same cable using Power over Ethernet. PoE+ (IEEE 802.3at) supplies up to 30 W per switch port, while IEEE 802.3bt raises that to 60 W for Type 3 and 90 W for Type 4. Some enterprise Wi-Fi 7 access points need more than PoE+ to run every radio and feature, so check the datasheet. Your switches also need multi-gigabit ports and a large enough total power budget to run every access point at once.",
      },
      {
        type: "h2",
        text: "When an upgrade pays off",
      },
      {
        type: "p",
        text: "Upgrading makes most sense when your access points are nearing the end of vendor support, when you have dense spaces such as meeting rooms, training rooms or open-plan floors, or when video calls and cloud applications struggle at busy times. If you already have a recent Wi-Fi 6 or 6E installation and complaints are rare, a survey and some tuning of channels and power levels often achieves more than new hardware.",
      },
      {
        type: "p",
        text: "Whatever you decide, start with a site survey. It shows where coverage is weak, how crowded each band is and whether the problem is wireless at all; a slow internet connection or an overloaded switch is often blamed on Wi-Fi.",
      },
      {
        type: "p",
        text: "If you are planning an office network refresh, our team can survey your site, check your cabling and recommend the right mix of upgrades.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Wi-Fi 7 pays off when your devices support it and your office is busy.",
          "Check cabling first: 2.5 Gbps over Cat5e suits most access points, while 10 Gbps needs Cat6A.",
          "Budget for power: some Wi-Fi 7 access points need more than PoE+ provides.",
        ],
      },
    ],
  },
  {
    cover: "lock",
    tag: "Cyber Security",
    date: "Aug 22, 2026",
    iso: "2026-08-22",
    read: 8,
    title: "A Ransomware Readiness Checklist for Small and Mid-Size Teams",
    text: "Backups you have never restored are not backups. Use this checklist to test your defences before an attacker does.",
    slug: "a-ransomware-readiness-checklist-for-small-and-mid-size-team",
    keywords: ["Ransomware", "Backup and recovery", "Incident response"],
    heroImage: "/assets/images/blog/ransomware-checklist-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Smaller organisations appeal to ransomware gangs for a simple reason: they often hold valuable data but have thinner defences. You do not need a large security team to be ready. You need a short list of controls that are genuinely in place, and proof that they work.",
      },
      {
        type: "h2",
        text: "Why readiness matters now",
      },
      {
        type: "p",
        text: "Verizon's 2026 Data Breach Investigations Report found ransomware in 48% of the breaches it analysed. It also found that 69% of victims declined to pay, which is only realistic when you can get your systems back without the attacker's help. The checklist below draws on the #StopRansomware Guide from CISA and its partner agencies, adapted for teams without a dedicated security department.",
      },
      {
        type: "h2",
        text: "1. Backups you have actually restored",
      },
      {
        type: "p",
        text: "Keep at least one copy of critical data offline or immutable, so that an attacker who gains administrator access cannot encrypt or delete it. The widely used 3-2-1 rule is a sensible baseline: three copies of your data, on two different types of storage, with one copy kept off-site. Above all, test restores on a schedule. Every quarter, pick a server or a set of files, restore it from backup, time how long it takes and note what went wrong.",
      },
      {
        type: "p",
        text: "Protect the backup system itself with separate administrator accounts and multi-factor authentication. Attackers look for backup consoles early, because destroying backups raises the pressure to pay.",
      },
      {
        type: "h2",
        text: "2. Multi-factor authentication where attackers get in",
      },
      {
        type: "p",
        text: "Turn on MFA for email, VPN and other remote access, cloud administration and any remote management tools your IT provider uses. Prefer phishing-resistant methods such as passkeys or hardware security keys for administrator accounts. Remove shared logins and disable accounts belonging to people who have left.",
      },
      {
        type: "h2",
        text: "3. Patch what faces the internet first",
      },
      {
        type: "p",
        text: "Firewalls, VPN gateways, remote desktop services and file-transfer tools are favourite entry points because anyone can reach them. Keep an inventory of everything exposed to the internet, subscribe to your vendors' security advisories and fix critical flaws within days, not months. CISA's Known Exploited Vulnerabilities catalogue lists flaws attackers are already using, which makes it a practical priority list. Never leave remote desktop (RDP) open directly to the internet.",
      },
      {
        type: "h2",
        text: "4. Limit how far an attacker can spread",
      },
      {
        type: "p",
        text: "Give staff standard user accounts for daily work and keep administrator rights for separate, dedicated accounts. Segment the network so that office PCs cannot reach servers, backups and management interfaces directly. Endpoint detection and response (EDR) tools on laptops and servers can spot and stop ransomware behaviour, and many providers offer them to smaller teams as an affordable managed service.",
      },
      {
        type: "h2",
        text: "5. Know who to call and what to do",
      },
      {
        type: "p",
        text: "Write a short incident response plan and keep a printed copy, because your systems may be unavailable when you need it. It should name who can decide to disconnect systems and list contacts for your IT provider, insurer, lawyers and bank, plus how you will reach staff and customers if email is down. Run a one-hour tabletop exercise at least once a year to walk through a realistic attack.",
      },
      {
        type: "h2",
        text: "6. Know your reporting duties",
      },
      {
        type: "p",
        text: "Regulators increasingly expect fast notification. In India, CERT-In's directions of April 2022 require service providers, intermediaries, data centres, companies and government bodies to report cyber incidents, ransomware included, within six hours of noticing them. The same directions require logs from ICT systems to be kept for a rolling 180 days within India. Check the rules in every country where you operate and keep the reporting contacts in your incident plan.",
      },
      {
        type: "h2",
        text: "7. Prepare people for the moment it happens",
      },
      {
        type: "p",
        text: "Phishing remains a common way in, so short, regular awareness training helps. Staff also need to know exactly what to do when something looks wrong: disconnect the device from the network by unplugging the cable or turning off Wi-Fi, leave it switched on unless told otherwise, and call a named contact straight away. A fast report can be the difference between one infected laptop and an encrypted network.",
      },
      {
        type: "h2",
        text: "If the worst happens",
      },
      {
        type: "p",
        text: "Isolate affected systems, preserve evidence and bring in incident response help early. Before anyone considers paying, check No More Ransom, a project launched in 2016 by Europol, the Dutch police and security companies, which offers free decryption tools for many ransomware strains. Law enforcement agencies, including the FBI, advise against paying, because payment does not guarantee you will get your data back and it funds further attacks.",
      },
      {
        type: "p",
        text: "If you would like to test your readiness, our team can run a backup restore test and a tabletop exercise with your staff.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Untested backups are the biggest gap, so restore something every quarter.",
          "MFA on remote access and fast patching of internet-facing systems block the most common ways in.",
          "A printed incident plan and clear reporting duties save critical hours.",
        ],
      },
    ],
  },
  {
    cover: "chip",
    tag: "AI & Automation",
    date: "Aug 11, 2026",
    iso: "2026-08-11",
    read: 6,
    title:
      "Rolling Out AI Copilots at Work: What IT Leaders Should Sort Out First",
    text: "Data access, licensing and clear usage rules decide whether a copilot helps or leaks. Start with these four decisions.",
    slug: "rolling-out-ai-copilots-at-work-what-it-leaders-should-sort-",
    keywords: ["AI copilots", "Data governance", "AI policy"],
    heroImage: "/assets/images/blog/ai-copilots-hero.jpg",
    content: [
      {
        type: "lead",
        text: "AI assistants built into email, documents and chat can save real time on drafting, summarising and searching. They also tend to expose problems that were already there, especially files shared with far more people than intended. Four decisions, made before rollout, decide which of those outcomes you get.",
      },
      {
        type: "h2",
        text: "Decision 1: What can the copilot see?",
      },
      {
        type: "p",
        text: "Workplace copilots answer questions by searching content the user can already reach. Microsoft, for example, says Microsoft 365 Copilot works within each person's existing permissions, so it can only draw on files, emails and chats that person could already open, and that it respects sensitivity labels applied through Microsoft Purview. That is reassuring, but it also means any oversharing becomes much easier to find. A salary spreadsheet sitting in a site open to the whole company was always a risk; a copilot can now bring it up in seconds.",
      },
      {
        type: "p",
        text: "Before rollout, review sites, shared drives, teams and groups that are open to everyone, label confidential content and tighten access to anything sensitive. Treat this as a data clean-up project with its own owner and deadline.",
      },
      {
        type: "h2",
        text: "Decision 2: Who gets a licence, and which one?",
      },
      {
        type: "p",
        text: "Pricing models differ between suites. Microsoft sells Microsoft 365 Copilot as a paid per-user add-on, while Google began including Gemini features in its Workspace business and enterprise plans in January 2025, alongside higher plan prices. Check current pricing and terms with your provider, because both have changed more than once.",
      },
      {
        type: "p",
        text: "Rather than licensing everyone at once, start with a pilot group drawn from roles that involve a lot of writing, meetings or research, such as sales, HR, finance and project management. Ask them to record what they use the copilot for and how much time it saves.",
      },
      {
        type: "h2",
        text: "Decision 3: What are the rules?",
      },
      {
        type: "p",
        text: "Staff will use AI tools whether or not you approve them. IBM's 2025 Cost of a Data Breach study found that 63% of the organisations it researched had no AI governance policies, and that organisations with high levels of shadow AI, meaning unapproved AI tools used by staff, paid on average $670,000 more per breach than those with little or none. An approved copilot with clear rules is far safer than a ban that pushes people towards personal accounts.",
      },
      {
        type: "p",
        text: "Write a one-page acceptable use policy that covers which AI tools are approved, what data must never be pasted into public AI services, the need to review AI output before it is sent or published, and how to report a problem. If you operate in the EU, the AI Act has required organisations that deploy AI systems to take steps to build AI literacy among their staff since February 2025.",
      },
      {
        type: "h2",
        text: "Decision 4: How will you measure success?",
      },
      {
        type: "p",
        text: "Agree what success looks like before the pilot starts: time saved on specific tasks, how many people are still using it after the first month, user satisfaction and any security issues. Combine usage reports from the admin console with short surveys. If the results justify it, expand in waves; if not, you have learned that cheaply.",
      },
      {
        type: "h2",
        text: "Check how your data is handled",
      },
      {
        type: "p",
        text: "Read the provider's data-protection terms before you sign. Microsoft, for instance, commits that neither the questions staff ask Microsoft 365 Copilot, nor its answers, nor the company data it reads are used to train the underlying foundation models. Confirm where data is processed and stored, how long interactions are kept and how they can be searched for compliance, and make sure the answers fit your obligations under laws such as India's Digital Personal Data Protection Act, 2023 or the EU's GDPR.",
      },
      {
        type: "p",
        text: "If you are planning a copilot rollout, our team can run a permissions and data-readiness review and help you design a measured pilot.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Fix oversharing first, because a copilot can find anything a user can open.",
          "Pilot with a small group and measure time saved before licensing everyone.",
          "A short, clear usage policy is safer than an outright ban.",
        ],
      },
    ],
  },
  {
    cover: "database",
    tag: "Cloud Computing",
    date: "Jul 24, 2026",
    iso: "2026-07-24",
    read: 9,
    title:
      "Choosing a Cloud Database: Managed Postgres, NoSQL or Data Warehouse?",
    text: "Each option shines for a different workload. A simple decision guide based on how your data is read, written and reported.",
    slug: "choosing-a-cloud-database-managed-postgres-nosql-or-data-war",
    keywords: ["Cloud databases", "PostgreSQL", "Data warehouse"],
    heroImage: "/assets/images/blog/cloud-database-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Picking a database is one of the few early technical decisions that is genuinely hard to undo. The good news is that most business workloads fall into three broad patterns, and each has a natural home in the cloud: a managed relational database such as PostgreSQL, a NoSQL database or a data warehouse.",
      },
      {
        type: "h2",
        text: "Start with how your data is used",
      },
      {
        type: "p",
        text: "Before comparing products, answer three questions. How is data written: as many small transactions, such as orders and bookings, or in large batches loaded on a schedule? How is it read: by looking up individual records, or by scanning millions of rows to answer a business question? And how strict must consistency be: can a reader briefly see slightly out-of-date data, or must every read reflect the latest write?",
      },
      {
        type: "p",
        text: "Systems that handle day-to-day transactions are known as OLTP (online transaction processing) workloads. Reporting and analysis over large volumes of history are OLAP (online analytical processing) workloads. Most organisations need both, and the mistake to avoid is forcing one database to do both jobs badly.",
      },
      {
        type: "h2",
        text: "Option 1: Managed PostgreSQL",
      },
      {
        type: "p",
        text: "PostgreSQL is a mature open-source relational database whose roots go back to the POSTGRES project at the University of California, Berkeley, in 1986. It is also developers' favourite: in Stack Overflow's 2025 Developer Survey, 55.6% of respondents used it, more than any other database, and it also led the rankings for the database developers most admire and most want to work with.",
      },
      {
        type: "p",
        text: "Relational databases organise data in tables linked by keys, enforce rules such as unique values and valid references between tables, and support ACID transactions, so a payment is either recorded completely or not at all. PostgreSQL also stores semi-structured data through its JSONB type and supports vector similarity search through the open-source pgvector extension, which is handy for AI features.",
      },
      {
        type: "p",
        text: "Every major cloud offers it as a managed service: Amazon RDS and Aurora on AWS, Azure Database for PostgreSQL, and Cloud SQL and AlloyDB on Google Cloud. The provider handles patching, backups and failover, while you choose the size. The PostgreSQL project releases a new major version every year and supports each one for five years, so plan upgrades as a routine task.",
      },
      {
        type: "p",
        text: "Choose it when your data has clear relationships, such as customers, orders and invoices, when you need transactions and strong consistency, and when you want the freedom to move between providers. It is the right default for most business applications.",
      },
      {
        type: "h2",
        text: "Option 2: NoSQL databases",
      },
      {
        type: "p",
        text: "NoSQL is an umbrella term for databases that do not use the relational table model. The most common kinds in the cloud are key-value and document databases, such as Amazon DynamoDB, Azure Cosmos DB and MongoDB Atlas. They spread data across many servers automatically, which lets them handle very high request volumes with predictable response times.",
      },
      {
        type: "p",
        text: "The trade-off is that you design the data model around your access patterns up front. Queries that combine many kinds of records, or ad-hoc questions you did not plan for, are harder than in SQL. Many NoSQL services also let you choose between strongly consistent reads and cheaper, eventually consistent ones, so make sure you know which your application needs.",
      },
      {
        type: "p",
        text: "Choose it when you have very high volumes of simple lookups by key, such as user sessions, shopping carts, device telemetry or game state, when the shape of your data varies from record to record, or when you need fast access from several regions at once.",
      },
      {
        type: "h2",
        text: "Option 3: A cloud data warehouse",
      },
      {
        type: "p",
        text: "Data warehouses such as Google BigQuery, Amazon Redshift, Snowflake and Microsoft Fabric are built for analysis rather than transactions. They store data by column instead of by row, which makes it quick to scan and summarise a few columns across billions of records, and most of them separate storage from compute so you can scale each one independently.",
      },
      {
        type: "p",
        text: "A warehouse is not meant to sit behind your application. Instead, data is copied in from operational systems, on a schedule or as a continuous stream, and then queried by reporting tools and analysts. Pricing usually depends on how much data you store and how much compute your queries use, so badly written queries cost real money.",
      },
      {
        type: "p",
        text: "Choose it when you need dashboards, financial reporting or analysis that combines data from several systems, or when reports are starting to slow down your operational database.",
      },
      {
        type: "h2",
        text: "A simple decision guide",
      },
      {
        type: "p",
        text: "If you are building a business application and are unsure, start with managed PostgreSQL. Add a NoSQL service for a specific high-volume component once you can name the access pattern it will serve. Add a data warehouse when reporting and analytics outgrow what you can safely run against production. Many mature architectures use all three, each for the job it does best.",
      },
      {
        type: "h2",
        text: "Questions to ask before you commit",
      },
      {
        type: "p",
        text: "Whichever option you pick, check how backups and point-in-time recovery work, which regions the service runs in, how you would export your data if you ever needed to leave and how pricing behaves as you grow. Run a small proof of concept with realistic data volumes and queries; a day of testing can save months of migration later.",
      },
      {
        type: "p",
        text: "If you are choosing a database for a new system or struggling with an existing one, our team can review your workload and recommend the right fit.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Managed PostgreSQL is the right default for most business applications.",
          "Use NoSQL for high-volume, predictable lookups, and design the data model around how it is accessed.",
          "Run analytics in a data warehouse instead of overloading your production database.",
        ],
      },
    ],
  },
  {
    cover: "cloud",
    tag: "Cloud Computing",
    date: "May 22, 2026",
    iso: "2026-05-22",
    read: 7,
    title: "Hybrid Cloud vs Multi-Cloud: Which Fits a Mid-Size Business?",
    text: "The terms are often mixed up. We compare complexity, cost and resilience so you can pick without the jargon.",
    slug: "hybrid-cloud-vs-multi-cloud-which-fits-a-mid-size-business",
    keywords: ["Hybrid cloud", "Multi-cloud", "Cloud strategy"],
    heroImage: "/assets/images/blog/hybrid-vs-multi-cloud-hero.jpg",
    content: [
      {
        type: "lead",
        text: "Hybrid cloud and multi-cloud are often used as if they mean the same thing. They do not, and the difference matters when you plan budgets, skills and resilience. Here is a plain-language comparison for a mid-size business.",
      },
      {
        type: "h2",
        text: "Two different ideas",
      },
      {
        type: "p",
        text: "Hybrid cloud combines your own infrastructure, such as servers in an office or a private data centre, with a public cloud, and connects the two so applications and data can work across both. NIST's definition of cloud computing (SP 800-145, 2011) describes a hybrid cloud as a combination of separate cloud environments, private or public, linked by technology that allows data and applications to move between them.",
      },
      {
        type: "p",
        text: "Multi-cloud means using services from more than one public cloud provider, for example running your main applications on Azure while using Google Cloud for analytics or AWS for a particular product. An organisation can be hybrid, multi-cloud, both or neither.",
      },
      {
        type: "h2",
        text: "What most organisations do",
      },
      {
        type: "p",
        text: "Both patterns are now common among larger organisations. In Flexera's 2026 State of the Cloud survey of more than 750 IT leaders, 73% combined public and private cloud, 88% used more than one cloud provider and only 14% relied on public cloud alone. For a mid-size business, though, the real question is not what is common but what is worth the extra effort.",
      },
      {
        type: "h2",
        text: "When hybrid makes sense",
      },
      {
        type: "p",
        text: "Hybrid is often the natural result of a gradual migration rather than a deliberate end state. It suits businesses with systems that must stay on-premises, such as factory or warehouse equipment that needs very low latency, legacy applications that cannot move yet, or data that regulation or contracts require you to keep in a particular place. Tools such as Azure Arc, AWS Outposts and Google Distributed Cloud extend cloud management and services into your own sites, so both sides can run with one set of processes.",
      },
      {
        type: "p",
        text: "The costs are real. You still maintain hardware, power and physical security on-premises, and you need secure, reliable connections between your sites and the cloud.",
      },
      {
        type: "h2",
        text: "When multi-cloud makes sense",
      },
      {
        type: "p",
        text: "Multi-cloud often happens by accident: one team picks a provider, an acquisition brings another and a SaaS product runs on a third. Done deliberately, it makes sense when one provider has a clearly better service for a specific need, when customers or regulators require a particular provider, or when you want to reduce dependence on a single vendor.",
      },
      {
        type: "p",
        text: "Spreading a single application across several providers for resilience sounds attractive but is hard to do well. It multiplies the skills your team needs, complicates security and monitoring, and adds data transfer charges between clouds. For most mid-size businesses, designing across multiple regions of one provider, with good backups held elsewhere, gives better resilience for less effort.",
      },
      {
        type: "h2",
        text: "Lock-in is easing",
      },
      {
        type: "p",
        text: "Changing providers is getting easier. In early 2024, Google Cloud, AWS and Microsoft Azure each announced that customers moving their data off the platform could do so without data transfer fees, subject to conditions. In the EU, the Data Act's cloud switching rules have applied since 12 September 2025: customers can start a switch with no more than two months' notice, the move should normally be completed within 30 days, and switching charges are banned outright from 12 January 2027. Until then, providers may only pass on their actual costs.",
      },
      {
        type: "p",
        text: "Portability still depends on your own design choices. Containers, Kubernetes, open-source databases such as PostgreSQL and infrastructure as code keep more of your stack movable, while provider-specific services trade some portability for convenience.",
      },
      {
        type: "h2",
        text: "How to decide",
      },
      {
        type: "p",
        text: "Start with one primary public cloud unless you have a clear reason not to. Keep on-premises only what genuinely needs to be there, and connect it properly instead of treating it as a separate world. Add a second provider when a specific service or requirement justifies it, and make sure identity, security policies, monitoring and cost reporting cover every environment you run.",
      },
      {
        type: "p",
        text: "If you are weighing hybrid or multi-cloud options, our team can map your workloads and recommend an approach that fits your size, skills and budget.",
      },
      {
        type: "callout",
        title: "Key takeaways",
        items: [
          "Hybrid mixes your own infrastructure with public cloud; multi-cloud uses more than one public provider.",
          "For most mid-size businesses, one primary cloud plus well-chosen exceptions is simpler and cheaper.",
          "Lock-in is easing, but portable design choices still matter.",
        ],
      },
    ],
  },
];

export const covers = {
  shield: (
    <>
      <circle
        cx="185"
        cy="115"
        r="94"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".22"
        strokeDasharray="3 7"
      />
      <circle
        cx="185"
        cy="115"
        r="68"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".28"
      />
      <circle cx="279" cy="115" r="5" fill="#d2a566" />
      <circle cx="91" cy="115" r="5" fill="#fff" fillOpacity=".7" />
      <circle cx="185" cy="21" r="4" fill="#d2a566" fillOpacity=".8" />
      <circle cx="118" cy="48" r="4" fill="#fff" fillOpacity=".5" />
      <circle cx="252" cy="182" r="4" fill="#fff" fillOpacity=".5" />
      <path
        d="M185 66l44 16v34c0 26-18 44-44 54-26-10-44-28-44-54V82z"
        fill="#d2a566"
        fillOpacity=".16"
        stroke="#d2a566"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M166 118l14 14 26-30"
        fill="none"
        stroke="#fff"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  cloud: (
    <>
      <path
        d="M96 128H142M238 120h38"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".5"
        strokeWidth="2"
        strokeDasharray="3 6"
        strokeLinecap="round"
      />
      <circle cx="90" cy="128" r="5" fill="#fff" fillOpacity=".7" />
      <circle cx="282" cy="120" r="5" fill="#d2a566" />
      <path
        d="M146 158h88a30 30 0 0 0 3-59.8 48 48 0 0 0-91-4A32 32 0 0 0 146 158z"
        fill="#d2a566"
        fillOpacity=".16"
        stroke="#d2a566"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M164 112l16 10 14-10 22 20"
        fill="none"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M203 132h13v-13"
        fill="none"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  server: (
    <>
      <path
        d="M130 115H86M240 115h44"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".5"
        strokeWidth="2"
        strokeDasharray="3 6"
        strokeLinecap="round"
      />
      <circle cx="80" cy="115" r="5" fill="#fff" fillOpacity=".7" />
      <circle cx="290" cy="115" r="5" fill="#d2a566" />
      <g fill="#d2a566" fillOpacity=".14" stroke="#d2a566" strokeWidth="3">
        <rect x="130" y="62" width="110" height="32" rx="9" />
        <rect x="130" y="99" width="110" height="32" rx="9" />
        <rect x="130" y="136" width="110" height="32" rx="9" />
      </g>
      <g fill="#d2a566">
        <circle cx="150" cy="78" r="4" />
        <circle cx="150" cy="115" r="4" />
        <circle cx="150" cy="152" r="4" />
      </g>
      <path
        d="M170 78h52M170 115h52M170 152h52"
        stroke="#fff"
        strokeOpacity=".4"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </>
  ),
  wifi: (
    <>
      <path
        d="M121.4 88.4A90 90 0 0 1 248.6 88.4"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".45"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M142.6 109.6A60 60 0 0 1 227.4 109.6"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".75"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M163.8 130.8A30 30 0 0 1 206.2 130.8"
        fill="none"
        stroke="#d2a566"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <circle cx="185" cy="152" r="9" fill="#fff" />
      <circle cx="92" cy="170" r="4" fill="#fff" fillOpacity=".5" />
      <circle cx="286" cy="60" r="4" fill="#d2a566" />
    </>
  ),
  lock: (
    <>
      <path
        d="M36 62h42M92 62h18M36 80h24M74 80h52M36 98h60M252 150h60M270 168h42M232 168h22M246 132h30"
        fill="none"
        stroke="#fff"
        strokeOpacity=".12"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M164 108V92a21 21 0 0 1 42 0v16"
        fill="none"
        stroke="#d2a566"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <rect
        x="152"
        y="106"
        width="66"
        height="56"
        rx="13"
        fill="#d2a566"
        fillOpacity=".2"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <circle cx="185" cy="130" r="6.5" fill="#fff" />
      <path
        d="M185 136v13"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </>
  ),
  chip: (
    <>
      <path
        d="M165 75V58M165 155V172M185 75V58M185 155V172M205 75V58M205 155V172M145 95H128M225 95H242M145 115H128M225 115H242M145 135H128M225 135H242"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".7"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <rect
        x="145"
        y="75"
        width="80"
        height="80"
        rx="14"
        fill="#d2a566"
        fillOpacity=".16"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <rect
        x="163"
        y="93"
        width="44"
        height="44"
        rx="8"
        fill="none"
        stroke="#fff"
        strokeOpacity=".45"
        strokeWidth="2"
      />
      <text
        x="185"
        y="123"
        textAnchor="middle"
        fontSize="22"
        fontWeight="800"
        fontFamily="Manrope, Arial, sans-serif"
        fill="#fff"
      >
        AI
      </text>
    </>
  ),
  database: (
    <>
      <path
        d="M96 100H132M238 100h36"
        fill="none"
        stroke="#d2a566"
        strokeOpacity=".5"
        strokeWidth="2"
        strokeDasharray="3 6"
        strokeLinecap="round"
      />
      <circle cx="90" cy="100" r="5" fill="#fff" fillOpacity=".7" />
      <circle cx="280" cy="100" r="5" fill="#d2a566" />
      <path
        d="M140 78v74c0 11 20 20 45 20s45-9 45-20V78"
        fill="#d2a566"
        fillOpacity=".14"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <path
        d="M140 103c0 11 20 20 45 20s45-9 45-20M140 128c0 11 20 20 45 20s45-9 45-20"
        fill="none"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <ellipse
        cx="185"
        cy="78"
        rx="45"
        ry="16"
        fill="#d2a566"
        fillOpacity=".3"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <circle cx="212" cy="112" r="3" fill="#fff" />
      <circle cx="212" cy="137" r="3" fill="#fff" />
    </>
  ),
  code: (
    <>
      <path
        d="M36 62h46M96 62h24M36 80h30M80 80h60M36 98h40M262 150h50M250 168h28M290 168h22M274 132h38"
        fill="none"
        stroke="#fff"
        strokeOpacity=".12"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M150 88l-36 27 36 27M220 88l36 27-36 27"
        fill="none"
        stroke="#d2a566"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M200 76l-30 78"
        fill="none"
        stroke="#fff"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </>
  ),
  network: (
    <>
      <g stroke="#d2a566" strokeOpacity=".55" strokeWidth="2">
        <path d="M185 115L108 68M185 115L262 68M185 115L88 152M185 115L282 152M185 115L185 34M185 115L185 196" />
        <path d="M108 68L88 152M262 68L282 152" strokeOpacity=".25" />
      </g>
      <g fill="#0b1a3a" stroke="#d2a566" strokeWidth="3">
        <circle cx="108" cy="68" r="10" />
        <circle cx="262" cy="68" r="10" />
        <circle cx="88" cy="152" r="10" />
        <circle cx="282" cy="152" r="10" />
        <circle cx="185" cy="34" r="8" />
        <circle cx="185" cy="196" r="8" />
      </g>
      <circle
        cx="185"
        cy="115"
        r="22"
        fill="#d2a566"
        fillOpacity=".2"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <circle cx="185" cy="115" r="8" fill="#fff" />
    </>
  ),
  dashboard: (
    <>
      <rect
        x="115"
        y="56"
        width="140"
        height="94"
        rx="12"
        fill="#d2a566"
        fillOpacity=".12"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <path
        d="M165 176h40M185 150v26"
        stroke="#d2a566"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <g fill="#fff" fillOpacity=".55">
        <rect x="135" y="112" width="14" height="22" rx="3" />
        <rect x="157" y="98" width="14" height="36" rx="3" />
        <rect x="179" y="106" width="14" height="28" rx="3" />
      </g>
      <rect x="201" y="84" width="14" height="50" rx="3" fill="#d2a566" />
      <path
        d="M135 92l30-12 28 10 42-18"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  mail: (
    <>
      <rect
        x="126"
        y="86"
        width="118"
        height="76"
        rx="12"
        fill="#d2a566"
        fillOpacity=".16"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <path
        d="M130 94l55 42 55-42"
        fill="none"
        stroke="#d2a566"
        strokeWidth="3"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle
        cx="246"
        cy="84"
        r="19"
        fill="#0b1a3a"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <path
        d="M246 74v12"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="246" cy="93" r="2.6" fill="#fff" />
    </>
  ),
  backup: (
    <>
      <rect
        x="122"
        y="104"
        width="126"
        height="62"
        rx="14"
        fill="#d2a566"
        fillOpacity=".16"
        stroke="#d2a566"
        strokeWidth="3"
      />
      <path
        d="M138 136h60"
        stroke="#fff"
        strokeOpacity=".4"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="218" cy="136" r="4" fill="#d2a566" />
      <circle cx="232" cy="136" r="4" fill="#fff" fillOpacity=".6" />
      <path
        d="M150 88a38 38 0 0 1 66-14"
        fill="none"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M219 62v14h-14"
        fill="none"
        stroke="#fff"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
};

/* Shared gradients for the illustrated covers. Render <CoverDefs /> once on any page that shows a Cover or HeroArt. */
export function CoverDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute" }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="gt9-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0b1a3a" />
          <stop offset="1" stopColor="#14264f" />
        </linearGradient>
        <radialGradient id="gt9-glow" cx="0.82" cy="0.1" r="0.7">
          <stop offset="0" stopColor="#d2a566" stopOpacity=".38" />
          <stop offset="1" stopColor="#d2a566" stopOpacity="0" />
        </radialGradient>
        <pattern
          id="gt9-gridpat"
          width="28"
          height="28"
          patternUnits="userSpaceOnUse"
        >
          <path d="M28 0H0V28" fill="none" stroke="#fff" strokeOpacity=".05" />
        </pattern>
      </defs>
    </svg>
  );
}

/* 370 x 230 card cover */
export function Cover({ type }) {
  return (
    <svg
      viewBox="0 0 370 230"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <rect width="370" height="230" fill="url(#gt9-bg)" />
      <rect width="370" height="230" fill="url(#gt9-gridpat)" />
      <rect width="370" height="230" fill="url(#gt9-glow)" />
      {covers[type]}
    </svg>
  );
}

/* Wide article hero image */
export function HeroArt({ type }) {
  return (
    <svg
      viewBox="0 0 1200 480"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <rect width="1200" height="480" fill="url(#gt9-bg)" />
      <rect width="1200" height="480" fill="url(#gt9-gridpat)" />
      <rect width="1200" height="480" fill="url(#gt9-glow)" />
      <g fill="none" stroke="#d2a566">
        <circle
          cx="600"
          cy="240"
          r="205"
          strokeOpacity=".2"
          strokeDasharray="4 10"
        />
        <circle cx="600" cy="240" r="305" strokeOpacity=".1" />
        <circle cx="600" cy="240" r="430" strokeOpacity=".06" />
      </g>
      <g fill="#d2a566">
        <circle cx="190" cy="120" r="5" fill="#fff" fillOpacity=".5" />
        <circle cx="1010" cy="360" r="5" />
        <circle cx="270" cy="392" r="4" fillOpacity=".6" />
        <circle cx="944" cy="92" r="4" fill="#fff" fillOpacity=".5" />
        <circle cx="110" cy="300" r="3" fill="#fff" fillOpacity=".4" />
        <circle cx="1100" cy="190" r="3" fillOpacity=".7" />
      </g>
      <g transform="translate(285.5 44.5) scale(1.7)">{covers[type]}</g>
    </svg>
  );
}

export const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
